import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Hand, MousePointer2, Link2, Link2Off, ZoomIn, ZoomOut, Maximize2,
  Save, Loader2, CloudOff, Cloud, HelpCircle, X, Grid3X3,
} from "lucide-react";
import { ToolMode, MIN_ZOOM, MAX_ZOOM } from "./types";

interface OrgChartToolbarProps {
  toolMode: ToolMode;
  setToolMode: (mode: ToolMode) => void;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onZoomToFit: () => void;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  onSave: () => void;
  connectingFrom: boolean;
  onCancelConnect: () => void;
  showHelp: boolean;
  onToggleHelp: () => void;
  snapToGrid: boolean;
  onToggleSnap: () => void;
  connectionCount: number;
  isReadOnly: boolean;
}

export function OrgChartToolbar({
  toolMode, setToolMode, zoom, onZoomIn, onZoomOut, onZoomReset, onZoomToFit,
  isSaving, hasUnsavedChanges, onSave, connectingFrom, onCancelConnect,
  showHelp, onToggleHelp, snapToGrid, onToggleSnap, connectionCount, isReadOnly
}: OrgChartToolbarProps) {
  return (
    <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 border-b bg-card/50 backdrop-blur-sm">
      {/* Tool Buttons */}
      {!isReadOnly && (
        <div className="flex items-center gap-0.5 border rounded-xl p-1 bg-muted/40 shadow-sm">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={toolMode === 'move' ? 'default' : 'ghost'}
                className={`h-8 px-3 rounded-lg text-xs gap-1.5 ${toolMode === 'move' ? 'shadow-sm' : ''}`}
                onClick={() => setToolMode('move')}
              >
                <Hand className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Verschieben</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>Elemente verschieben (M) · Leertaste+Drag = Canvas verschieben</p></TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={toolMode === 'select' ? 'default' : 'ghost'}
                className={`h-8 px-3 rounded-lg text-xs gap-1.5 ${toolMode === 'select' ? 'shadow-sm' : ''}`}
                onClick={() => setToolMode('select')}
              >
                <MousePointer2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Auswählen</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>Elemente auswählen und bearbeiten (S)</p></TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={toolMode === 'connect' ? 'default' : 'ghost'}
                className={`h-8 px-3 rounded-lg text-xs gap-1.5 ${toolMode === 'connect' ? 'shadow-sm' : ''}`}
                onClick={() => setToolMode('connect')}
              >
                <Link2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Verbinden</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>Verbindungen erstellen (C)</p></TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Snap Toggle */}
      {!isReadOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant={snapToGrid ? 'default' : 'ghost'}
              className={`h-8 px-2.5 rounded-lg ${snapToGrid ? 'shadow-sm' : ''}`}
              onClick={onToggleSnap}
            >
              <Grid3X3 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent><p>{snapToGrid ? 'Raster deaktivieren' : 'Am Raster ausrichten'}</p></TooltipContent>
        </Tooltip>
      )}

      {/* Zoom Controls */}
      <div className="flex items-center gap-0.5 border rounded-xl p-1 bg-muted/40 shadow-sm">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 px-2 rounded-lg" onClick={onZoomOut} disabled={zoom <= MIN_ZOOM}>
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent><p>Verkleinern</p></TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 px-2.5 min-w-[52px] rounded-lg text-xs font-mono" onClick={onZoomReset}>
              {Math.round(zoom * 100)}%
            </Button>
          </TooltipTrigger>
          <TooltipContent><p>Zoom zurücksetzen</p></TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 px-2 rounded-lg" onClick={onZoomIn} disabled={zoom >= MAX_ZOOM}>
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent><p>Vergrößern</p></TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 px-2 rounded-lg" onClick={onZoomToFit}>
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent><p>Einpassen</p></TooltipContent>
        </Tooltip>
      </div>

      {/* Save */}
      {!isReadOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant={hasUnsavedChanges ? "default" : "outline"}
              className="h-8 px-3 rounded-lg text-xs gap-1.5"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                hasUnsavedChanges ? <CloudOff className="h-3.5 w-3.5" /> :
                  <Cloud className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">
                {isSaving ? "Speichert..." : hasUnsavedChanges ? "Speichern" : "Gespeichert"}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent><p>{hasUnsavedChanges ? "Änderungen speichern" : "Alle Änderungen gespeichert"}</p></TooltipContent>
        </Tooltip>
      )}

      {/* Connection status */}
      {!isReadOnly && connectingFrom && (
        <Badge variant="secondary" className="bg-green-500/15 text-green-700 dark:text-green-300 rounded-lg gap-1.5 px-2.5">
          <Link2 className="h-3 w-3" />
          Verbindungsziel wählen...
          <button onClick={onCancelConnect} className="ml-1 hover:bg-destructive/20 rounded p-0.5">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      )}

      {!isReadOnly && connectionCount > 0 && (
        <Badge variant="outline" className="rounded-lg text-[10px] gap-1">
          <Link2 className="h-3 w-3" />
          {connectionCount}
        </Badge>
      )}

      {/* Help */}
      {!isReadOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 ml-auto rounded-lg" onClick={onToggleHelp}>
              <HelpCircle className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent><p>Hilfe</p></TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
